// 全局对话框 (2026-09-12): 取代 window.alert / confirm / prompt 的原生样式.
// 命令式 API (Promise), 由 <DialogHost/> (App.tsx 挂一次) 订阅并渲染; 同一时刻只显示一个, 后来的排队.

export type DialogKind = 'alert' | 'confirm' | 'prompt';

export interface DialogRequest {
    id: number;
    kind: DialogKind;
    message: string;
    title?: string;
    okText?: string;
    cancelText?: string;
    /** 危险操作 (删除 / 移出 / 吊销): 确认按钮红色 */
    danger?: boolean;
    /** prompt 专用 */
    defaultValue?: string;
    placeholder?: string;
    resolve: (v: unknown) => void;
}

type Listener = (queue: DialogRequest[]) => void;
let queue: DialogRequest[] = [];
let listener: Listener | null = null;
let seq = 0;

function push(req: Omit<DialogRequest, 'id'>): void {
    queue = [...queue, { ...req, id: ++seq }];
    listener?.(queue);
}

/** DialogHost 内部用: 订阅队列; 返回取消订阅函数. */
export function subscribeDialogs(fn: Listener): () => void {
    listener = fn;
    fn(queue);
    return () => {
        if (listener === fn) listener = null;
    };
}

/** DialogHost 内部用: 结束队首对话框并回传结果. */
export function settleDialog(id: number, value: unknown): void {
    const req = queue.find((q) => q.id === id);
    queue = queue.filter((q) => q.id !== id);
    listener?.(queue);
    req?.resolve(value);
}

export interface ConfirmOptions {
    title?: string;
    okText?: string;
    cancelText?: string;
    danger?: boolean;
}

/** 确认框: resolve true = 确定. 文案里若含 "删除/移出/吊销/撤销/清除/解除/驳回" 自动按危险操作着色. */
export function confirmDialog(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
    const danger = opts.danger ?? /删除|移出|吊销|撤销|清除|解除|驳回|中断|放弃/.test(message);
    return new Promise((resolve) => {
        push({ kind: 'confirm', message, title: opts.title, okText: opts.okText, cancelText: opts.cancelText, danger, resolve: (v) => resolve(!!v) });
    });
}

/** 提示框: 只有一个"知道了". 返回 Promise 但通常无需 await. */
export function alertDialog(message: string, opts: { title?: string } = {}): Promise<void> {
    return new Promise((resolve) => {
        push({ kind: 'alert', message, title: opts.title, resolve: () => resolve() });
    });
}

/** 输入框: resolve 输入的字符串, 取消为 null (与 window.prompt 同语义). */
export function promptDialog(
    message: string,
    defaultValue = '',
    opts: { title?: string; placeholder?: string; okText?: string } = {},
): Promise<string | null> {
    return new Promise((resolve) => {
        push({
            kind: 'prompt', message, defaultValue, title: opts.title, placeholder: opts.placeholder, okText: opts.okText,
            resolve: (v) => resolve(v === null || v === undefined ? null : String(v)),
        });
    });
}
